/**
 * LA UBICACIÓN POR GPS al reportar: nave (el polígono que contiene el punto),
 * sector y línea sugerida (`placeInGreenhouse`). Para que el trabajador no tenga
 * que pulsar cuatro pantallas: se le lleva a la línea, marcada, y confirma con
 * un toque; las pastillas de arriba dejan cambiar cualquier cosa.
 *
 * Solo dentro de su alcance (fincas permitidas, sectores excluidos) y solo con
 * un GPS razonable (`MAX_ACCURACY_M`). Nada es fatal: sin adivinanza, el camino
 * de siempre. Si VARIAS naves contienen el punto (solapadas), decide
 * `chooseGreenhouse`: la que sepa decir el sector, y de esas la más adentro.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { distanceToRing, pointInRing, projectWgs84ToGreenhouseLocal, ringFromGeoPolygon, type XY } from "@/lib/geo/wgs84Local";
import { MAX_ACCURACY_M, chooseGreenhouse, type GreenhouseHit, type PlacementBlock, type PlacementRow } from "@/lib/pests/gpsPlacement";

export interface GpsPlacement {
  locationId: string | null;
  greenhouseId: string;
  blockId: string | null;
  lineNumber: number | null;
  rowId: string | null;
  distanceM: number | null;
  accuracyM: number;
}

interface GreenhouseGeo {
  id: string;
  location_id: string | null;
  geometry: { geoPolygonWgs84?: unknown; perimeter?: XY[] } | null;
  centroid_lat: number | null;
  centroid_lng: number | null;
}

export function useGpsPlacement(
  gps: { lat: number; lng: number; accuracyM: number } | null,
  allowedLocationIds: Set<string> | null,
  excludedBlockIds: Set<string> | null,
  enabled: boolean,
) {
  const { company } = useAuth();
  const usable = !!gps && gps.accuracyM <= MAX_ACCURACY_M;

  const naves = useQuery({
    queryKey: ["gps-placement", "greenhouses", company?.id],
    enabled: enabled && usable && !!company?.id,
    staleTime: 5 * 60_000,
    retry: 1,
    queryFn: async () => {
      const { data } = await supabase.from("greenhouses").select("id, location_id, geometry, centroid_lat, centroid_lng");
      return ((data ?? []) as unknown as GreenhouseGeo[]);
    },
  });

  // Las naves que contienen el punto, cada una con el punto en SU marco.
  const hits = useMemo(() => {
    const out: (GreenhouseHit & { greenhouse: GreenhouseGeo })[] = [];
    if (!gps || !usable) return out;
    for (const g of naves.data ?? []) {
      if (allowedLocationIds && g.location_id && !allowedLocationIds.has(g.location_id)) continue;
      const ring = ringFromGeoPolygon(g.geometry?.geoPolygonWgs84);
      if (ring.length < 3 || g.centroid_lat == null || g.centroid_lng == null) continue;
      const local = projectWgs84ToGreenhouseLocal({ lat: gps.lat, lng: gps.lng }, { centroid: { lat: g.centroid_lat, lng: g.centroid_lng }, ringWgs84: ring });
      const perimeter = g.geometry?.perimeter ?? [];
      if (local && perimeter.length >= 3 && pointInRing(local, perimeter)) {
        out.push({ id: g.id, local, depth: distanceToRing(local, perimeter), greenhouse: g });
      }
    }
    return out;
  }, [gps, usable, naves.data, allowedLocationIds]);
  const hitIds = hits.map((h) => h.id);

  const detalle = useQuery({
    queryKey: ["gps-placement", "blocks", hitIds.join(",")],
    enabled: hitIds.length > 0,
    staleTime: 60_000,
    retry: 1,
    queryFn: async () => {
      const { data: blocks } = await supabase.from("blocks").select("id, greenhouse_id, geometry").in("greenhouse_id", hitIds).is("archived_at", null);
      const ids = (blocks ?? []).map((b) => b.id);
      const { data: rows } = ids.length
        ? await supabase.from("block_rows").select("id, block_id, line_number, geometry").in("block_id", ids).eq("is_active", true)
        : { data: [] as unknown[] };
      const pb: (PlacementBlock & { greenhouseId: string })[] = (blocks ?? []).map((b) => ({
        id: b.id,
        greenhouseId: b.greenhouse_id as string,
        points: ((b.geometry as { points?: XY[] } | null)?.points ?? []),
      }));
      const pr: PlacementRow[] = ((rows ?? []) as { id: string; block_id: string; line_number: number; geometry: { a?: XY; b?: XY } | null }[])
        .filter((r) => r.geometry?.a && r.geometry?.b)
        .map((r) => ({ id: r.id, block_id: r.block_id, line_number: r.line_number, a: r.geometry!.a!, b: r.geometry!.b! }));
      return { blocks: pb, rows: pr };
    },
  });

  const placement = useMemo<GpsPlacement | null>(() => {
    if (hits.length === 0 || !gps) return null;
    const d = detalle.data;
    if (!d) return null;
    const blocks = excludedBlockIds?.size ? d.blocks.filter((b) => !excludedBlockIds.has(b.id)) : d.blocks;
    const chosen = chooseGreenhouse(hits, blocks, d.rows);
    if (!chosen) return null;
    const greenhouse = hits.find((h) => h.id === chosen.hit.id)!.greenhouse;
    return {
      locationId: greenhouse.location_id,
      greenhouseId: greenhouse.id,
      blockId: chosen.placement?.blockId ?? null,
      lineNumber: chosen.placement?.lineNumber ?? null,
      rowId: chosen.placement?.rowId ?? null,
      distanceM: chosen.placement?.distanceM ?? null,
      accuracyM: gps.accuracyM,
    };
  }, [hits, gps, detalle.data, excludedBlockIds]);

  return { placement, loading: usable && (naves.isLoading || (hits.length > 0 && detalle.isLoading)) };
}
